class ProductPersonalizeController extends HTMLElement {
  constructor () {
    super();
    this._initialized = false;
    this._steps = [];
    this._currentIndex = 0;
    this._sectionEl = null;
    this._navSteps = [];
    this._titleEl = null;
    this._stepsNavEl = null;
    this._isMobile = typeof window !== 'undefined'
      ? (window.innerWidth || document.documentElement.clientWidth) < 750
      : false;

    this._resizeHandler = this._handleResize.bind(this);
  }

  connectedCallback () {
    if (this._initialized) return;
    this._initialized = true;

    this._sectionEl = this.closest('section.section-personalize-product') || null;

    if (this._sectionEl) {
      this._stepsNavEl = this._sectionEl.querySelector('.product-personalize__steps') || null;
    }

    this._titleEl =
      this.querySelector('.product__title') ||
      (this._sectionEl && this._sectionEl.querySelector('.product__title')) ||
      null;

    this._steps = Array.from(this.querySelectorAll('[data-step]')).sort((a, b) => {
      const sa = Number(a.getAttribute('data-step')) || 0;
      const sb = Number(b.getAttribute('data-step')) || 0;
      return sa - sb;
    });

    if (!this._steps.length) return;

    this._steps.forEach(step => {
      step.classList.remove('is-active', 'is-leaving');
    });

    if (this._sectionEl) {
      this._navSteps = Array.from(
        this._sectionEl.querySelectorAll('.product-personalize__steps .product-personalize__step'),
      );

      this._navSteps.forEach((item, index) => {
        item.addEventListener('click', (event) => {
          event.preventDefault();
          this.goToStep(index);
          this._scrollToAnchorIfNeeded();
        });
      });
    }

    this._currentIndex = 0;

    this._updateLayoutMode();
    window.addEventListener('resize', this._resizeHandler);

    const hasStepQuery = this._hasStepQuery();
    this._applyStepFromQuery();

    if (hasStepQuery) {
      this.classList.add('product-personalize', 'product-personalize--no-animate');
      this._showStep(this._currentIndex, { immediate: true });
      requestAnimationFrame(() => {
        this.classList.remove('product-personalize--no-animate');
      });
    } else {
      this._showStep(this._currentIndex, { immediate: true });
    }

    this._steps.forEach((step, index) => {
      const nextBtn = step.querySelector('button[name="next"]');
      if (!nextBtn) return;

      if (index === this._steps.length - 1) return;

      nextBtn.addEventListener('click', (event) => {
        event.preventDefault();

        const { valid, firstInvalidField } = this._validateStep(index, { silent: false });
        if (!valid) {
          if (firstInvalidField) {
            this._scrollElementIntoViewWithHeaderOffset(firstInvalidField);
            if (typeof firstInvalidField.focus === 'function') {
              firstInvalidField.focus();
            }
          }
          return;
        }

        this.goToStep(index + 1);

        this._scrollToAnchorIfNeeded();
      });
    });

    const lastStep = this._steps[this._steps.length - 1];
    if (lastStep) {
      const finalBtn = lastStep.querySelector(
        'button[type="submit"], button[name="add"], .product-form__submit',
      );

      if (finalBtn) {
        finalBtn.addEventListener('click', (event) => {
          const invalidInfo = this._findFirstInvalidStep();
          if (!invalidInfo) {
            return;
          }

          event.preventDefault();

          const { stepIndex, field } = invalidInfo;

          this.goToStep(stepIndex);

          this._scrollToAnchorIfNeeded();

          setTimeout(() => {
            if (field) {
              this._scrollElementIntoViewWithHeaderOffset(field);
              if (typeof field.reportValidity === 'function') {
                field.reportValidity();
              }
              if (typeof field.focus === 'function') {
                field.focus();
              }
            }
          }, 300);
        });
      }
    }
  }

  disconnectedCallback () {
    window.removeEventListener('resize', this._resizeHandler);
  }

  goToStep (index) {
    if (index < 0 || index >= this._steps.length) return;
    if (index === this._currentIndex) return;

    this._currentIndex = index;
    this._showStep(this._currentIndex);
  }

  nextStep () {
    this.goToStep(this._currentIndex + 1);
  }

  _showStep (index, options = {}) {
    const { immediate = false } = options;

    this._steps.forEach((step, i) => {
      const isActive = i === index;

      if (isActive) {
        step.classList.add('is-active');
        step.classList.remove('is-leaving');
      } else {
        if (!immediate && step.classList.contains('is-active')) {
          step.classList.add('is-leaving');
          step.classList.remove('is-active');
          setTimeout(() => step.classList.remove('is-leaving'), 250);
        } else {
          step.classList.remove('is-active', 'is-leaving');
        }
      }
    });

    if (this._sectionEl) {
      if (index > 0) {
        this._sectionEl.classList.add('product-personalize-started');
      } else {
        this._sectionEl.classList.remove('product-personalize-started');
      }
    }

    if (this._navSteps && this._navSteps.length) {
      this._navSteps.forEach((item, i) => {
        item.classList.toggle('product-personalize__step--active', i === index);
        item.classList.toggle('product-personalize__step--completed', i < index);
      });
    }

    this._updateStepInUrl(index);

    this.dispatchEvent(
      new CustomEvent('step-change', {
        detail: {
          index,
          step: this._steps[index],
          stepNumber: Number(this._steps[index].getAttribute('data-step')) || index + 1,
        },
      }),
    );
  }

  _validateStep (index, { silent = false } = {}) {
    const step = this._steps[index];
    if (!step) {
      return { valid: true, firstInvalidField: null };
    }

    const requiredFields = Array.from(step.querySelectorAll('[required]'));
    if (!requiredFields.length) {
      return { valid: true, firstInvalidField: null };
    }

    for (const field of requiredFields) {
      if (!field.checkValidity()) {
        if (!silent && typeof field.reportValidity === 'function') {
          field.reportValidity();
        }
        return { valid: false, firstInvalidField: field };
      }
    }

    return { valid: true, firstInvalidField: null };
  }

  _findFirstInvalidStep () {
    for (let i = 0; i < this._steps.length; i++) {
      const { valid, firstInvalidField } = this._validateStep(i, { silent: true });
      if (!valid) {
        return { stepIndex: i, field: firstInvalidField };
      }
    }
    return null;
  }

  _hasStepQuery () {
    if (typeof window === 'undefined') return false;
    try {
      const url = new URL(window.location.href);
      return url.searchParams.has('step');
    } catch (e) {
      return false;
    }
  }

  _applyStepFromQuery () {
    if (typeof window === 'undefined') return;

    try {
      const url = new URL(window.location.href);
      const stepParam = url.searchParams.get('step');
      if (!stepParam) return;

      const parsed = parseInt(stepParam, 10);
      if (!Number.isFinite(parsed)) return;

      const stepIndex = parsed - 1; // query 1-based → index 0-based
      if (stepIndex >= 0 && stepIndex < this._steps.length) {
        this._currentIndex = stepIndex;
      }
    } catch (e) {
    }
  }

  _updateStepInUrl (index) {
    if (typeof window === 'undefined' || !window.history || !window.history.replaceState) return;

    try {
      const url = new URL(window.location.href);
      const stepNumber = index + 1;

      if (stepNumber === 1) {
        url.searchParams.delete('step');
      } else {
        url.searchParams.set('step', String(stepNumber));
      }

      window.history.replaceState(null, '', url.toString());
    } catch (e) {
    }
  }

  _updateLayoutMode () {
    if (typeof window === 'undefined') return;
    const width = window.innerWidth || document.documentElement.clientWidth;
    this._isMobile = width < 750;
  }

  _handleResize () {
    this._updateLayoutMode();
  }

  _scrollToAnchorIfNeeded () {
    if (typeof window === 'undefined') return;

    let targetEl = null;
    if (this._isMobile) {
      targetEl = this._titleEl || this._stepsNavEl;
    } else {
      targetEl = this._stepsNavEl || this._titleEl;
    }

    if (!targetEl) return;

    requestAnimationFrame(() => {
      const rect = targetEl.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

      const isVisible = rect.top >= 0 && rect.top < viewportHeight;
      if (isVisible) return;

      let headerOffset = 0;
      try {
        const rootStyles = getComputedStyle(document.documentElement);
        const headerVar = rootStyles.getPropertyValue('--header-height').trim();
        if (headerVar) {
          const parsed = parseFloat(headerVar);
          if (!Number.isNaN(parsed)) {
            headerOffset = parsed;
          }
        }
      } catch (e) {
        // игнорим
      }

      const extraOffset = 20;
      const scrollTop = window.scrollY + rect.top - headerOffset - extraOffset;

      window.scrollTo({
        top: scrollTop,
        behavior: 'smooth',
      });
    });
  }

  _scrollElementIntoViewWithHeaderOffset (el) {
    if (typeof window === 'undefined') return;
    if (!el) return;

    requestAnimationFrame(() => {
      const rect = el.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

      let headerOffset = 0;
      try {
        const rootStyles = getComputedStyle(document.documentElement);
        const headerVar = rootStyles.getPropertyValue('--header-height').trim();
        if (headerVar) {
          const parsed = parseFloat(headerVar);
          if (!Number.isNaN(parsed)) {
            headerOffset = parsed;
          }
        }
      } catch (e) {
      }

      const extraOffset = 20;
      const topLimit = headerOffset + extraOffset;
      const isVisibleEnough =
        rect.top >= topLimit && rect.bottom <= viewportHeight;

      if (isVisibleEnough) return;

      const scrollTop = window.scrollY + rect.top - headerOffset - extraOffset;

      window.scrollTo({
        top: scrollTop,
        behavior: 'smooth',
      });
    });
  }
}

if (!customElements.get('product-personalize-controller')) {
  customElements.define('product-personalize-controller', ProductPersonalizeController);
}
